/**
 * size-gate.js
 *
 * Für Produkte mit einer "Wähle deine Größe"-Option:
 * - Solange der Nutzer keine Größe aktiv gewählt hat, zeigen ALLE Add-to-Cart-Buttons
 *   (Haupt-Button UND Sticky-Bar-Button) "Größe wählen".
 * - Klick auf einen der Buttons ohne Auswahl → scrollt zum Varianten-Picker und hebt ihn hervor.
 * - Sobald Nutzer eine Größe wählt, wechseln ALLE Buttons auf "In den Warenkorb".
 * - Die gewählte Größe wird direkt hinter dem Produkttitel in der Sticky-Bar angehängt
 *   (Format: "Produktname - Größe: S").
 *
 * Aktiviert sich automatisch, wenn <variant-picker> das Attribut `requires-user-selection` hat.
 */
(function () {
  'use strict';

  const CHOOSE_SIZE_LABEL = 'Größe wählen';
  const SIZE_DISPLAY_PREFIX = ' - Größe: ';

  const getChooseSizeLabel = () => {
    if (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.chooseSizeButton) {
      return window.themeVariables.strings.chooseSizeButton;
    }
    return CHOOSE_SIZE_LABEL;
  };

  const getAddToCartLabel = () => {
    if (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.addToCartButton) {
      return window.themeVariables.strings.addToCartButton;
    }
    return 'In den Warenkorb';
  };

  function initPicker(picker) {
    if (!picker || picker.dataset.sizeGateInit === 'true') return;
    picker.dataset.sizeGateInit = 'true';

    const formId = picker.getAttribute('form');
    const form = formId ? document.forms[formId] : picker.closest('form');
    if (!form) return;

    function getAllSubmitButtons() {
      const selector = formId ? `buy-buttons[form="${formId}"]` : 'buy-buttons';
      const buyButtonsList = document.querySelectorAll(selector);
      const buttons = [];
      buyButtonsList.forEach((bb) => {
        const btn = bb.querySelector('button[type="submit"]');
        if (btn) buttons.push(btn);
      });
      if (buttons.length === 0) {
        form.querySelectorAll('button[type="submit"]').forEach((btn) => buttons.push(btn));
      }
      return buttons;
    }

    const sizeFieldset = picker.querySelector('[data-size-gate-option]');
    if (!sizeFieldset) return;

    // Wichtig: Wir hören Klicks NICHT am ganzen Fieldset, sondern nur am
    // tatsächlichen Options-Container (data-option-selector). Das Fieldset enthält
    // u.a. den Größentabellen-Trigger und das Größentabellen-Modal als Geschwister
    // des Options-Containers — Klicks darin sollen NICHT als Größenauswahl gelten.
    const optionsContainer = sizeFieldset.querySelector('[data-option-selector]');
    if (!optionsContainer) return;

    // Früher: Bei ?variant=... in der URL wurde userSelected = true gesetzt.
    // Bewusst geändert: Auch bei direkten Variant-Links muss der Kunde die Größe
    // aktiv bestätigen (zur weiteren Reduktion der Größen-Retouren).
    let userSelected = false;

    function markUserSelected() {
      if (userSelected) return;
      userSelected = true;
      syncAllButtons();
      syncStickyBarSize();
    }

    function syncAllButtons() {
      getAllSubmitButtons().forEach(syncButton);
    }

    function syncButton(submitBtn) {
      if (!userSelected) {
        submitBtn.innerHTML = `<span>${escapeHtml(getChooseSizeLabel())}</span>`;
        submitBtn.dataset.sizeGateBlocked = 'true';
        submitBtn.setAttribute('aria-disabled', 'false');
      } else {
        submitBtn.dataset.sizeGateBlocked = 'false';
        if (submitBtn.innerHTML.includes(getChooseSizeLabel())) {
          submitBtn.innerHTML = `<span>${escapeHtml(getAddToCartLabel())}</span>`;
        }
      }
    }

    // --- Gewählte Größe direkt hinter dem Produkttitel anhängen ---
    function syncStickyBarSize() {
      const stickyBar = document.querySelector(`product-sticky-bar[form="${formId}"]`);
      if (!stickyBar) return;

      const infoContainer = stickyBar.querySelector('.product-sticky-bar__info .v-stack');
      if (!infoContainer) return;

      // Aufräumen: Falls ein früherer Ansatz einen Preis-Zeilen-Wrapper erstellt hat,
      // diesen zurückbauen, damit kein verwaister Flex-Container im DOM bleibt.
      const legacyRow = infoContainer.querySelector('[data-size-gate-price-row]');
      if (legacyRow) {
        while (legacyRow.firstChild) {
          // Display-Element nicht mit zurückwandern lassen - es wird am Titel neu angehängt
          if (legacyRow.firstChild.hasAttribute && legacyRow.firstChild.hasAttribute('data-size-gate-display')) {
            legacyRow.removeChild(legacyRow.firstChild);
          } else {
            legacyRow.parentNode.insertBefore(legacyRow.firstChild, legacyRow);
          }
        }
        legacyRow.remove();
      }

      const titleEl = infoContainer.querySelector('.h6');
      if (!titleEl) return;

      // Display-Element: anlegen oder wiederverwenden, direkt als Kind des Titel-Elements
      let displayEl = titleEl.querySelector('[data-size-gate-display]');
      if (!displayEl) {
        displayEl = document.createElement('span');
        displayEl.setAttribute('data-size-gate-display', '');
        displayEl.className = 'size-gate-display';
        titleEl.appendChild(displayEl);
      }

      if (!userSelected) {
        displayEl.textContent = '';
        displayEl.hidden = true;
        return;
      }

      // Aktuelle Größe aus dem Größen-Fieldset auslesen
      let sizeValue = '';
      const hiddenInput = sizeFieldset.querySelector('input[type="hidden"][name^="option"]');
      if (hiddenInput && hiddenInput.value) {
        sizeValue = hiddenInput.value;
      } else {
        const checked = sizeFieldset.querySelector('input[name^="option"]:checked');
        if (checked) sizeValue = checked.value;
      }

      if (sizeValue) {
        displayEl.textContent = SIZE_DISPLAY_PREFIX + sizeValue;
        displayEl.hidden = false;
      } else {
        displayEl.textContent = '';
        displayEl.hidden = true;
      }
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function attachClickGuards() {
      getAllSubmitButtons().forEach((submitBtn) => {
        if (submitBtn.dataset.sizeGateGuardAttached === 'true') return;
        submitBtn.dataset.sizeGateGuardAttached = 'true';

        submitBtn.addEventListener('click', function (e) {
          if (submitBtn.dataset.sizeGateBlocked === 'true') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            highlightSizeSelector();
          }
        }, true);
      });
    }

    syncAllButtons();
    syncStickyBarSize();
    attachClickGuards();

    // --- Dropdown-Fix: Den sichtbaren Trigger-Text im Dropdown updaten ---
    // Prestige updatet den Span via `aria-activedescendant`-Mutation-Callback, aber
    // NUR wenn oldValue !== null. Bei Größen-Gate haben wir initial keine Option mit
    // aria-selected="true", daher ist oldValue beim ersten Klick null → Prestige
    // überspringt den Update. Wir machen es selbst.
    function syncDropdownTriggerText(optionEl) {
      if (!optionEl) return;
      const listbox = optionEl.closest('x-listbox[aria-owns]');
      if (!listbox) return;
      const ariaOwns = listbox.getAttribute('aria-owns');
      if (!ariaOwns) return;
      ariaOwns.split(/\s+/).forEach((id) => {
        const targetEl = document.getElementById(id);
        if (targetEl) {
          targetEl.textContent = optionEl.getAttribute('title') || optionEl.innerText || optionEl.value;
        }
      });
    }

    // --- Auswahl-Erkennung ---
    // Wir hören Klicks am optionsContainer (data-option-selector), nicht am ganzen
    // Fieldset. Damit werden Klicks auf den Größentabellen-Link oder Modal-Inhalte
    // NICHT als Größenauswahl interpretiert.

    optionsContainer.addEventListener('click', function (e) {
      // Nur echte Options-Elemente zählen als Auswahl.
      const target = e.target.closest(
        'input[type="radio"], [role="option"], [data-option-value], .swatch, .block-swatch'
      );
      if (!target) return;

      // Sicherheitscheck: Wenn das gefundene Element KEINEN value hat und kein
      // data-option-value trägt, ist es wahrscheinlich kein echter Options-Klick.
      const hasValue = target.value || target.getAttribute('data-option-value') !== null || target.hasAttribute('data-option-value');
      if (!hasValue && target.getAttribute('role') !== 'option') return;

      // Dropdown-Listbox-Option: Trigger-Text syncen + Listbox als berührt markieren
      if (target.getAttribute('role') === 'option') {
        syncDropdownTriggerText(target);
        const listbox = target.closest('x-listbox[data-size-gate-listbox]');
        if (listbox) listbox.setAttribute('data-user-interacted', 'true');
      }
      // Swatch-Container als berührt markieren (für CSS-Reset der initialen Markierung)
      const swatchContainer = target.closest('[data-size-gate-swatches]');
      if (swatchContainer) swatchContainer.setAttribute('data-user-interacted', 'true');
      markUserSelected();
    }, true);

    optionsContainer.addEventListener('change', function () {
      markUserSelected();
    });

    optionsContainer.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        // Auch hier: nur echte Options-Elemente, kein generisches 'button'
        const target = e.target.closest('input[type="radio"], [role="option"], [data-option-value]');
        if (target) markUserSelected();
      }
    }, true);

    optionsContainer.querySelectorAll('input[type="hidden"][name^="option"]').forEach((input) => {
      input.addEventListener('input', markUserSelected);
      input.addEventListener('change', markUserSelected);
    });

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'aria-selected' && m.target.getAttribute('aria-selected') === 'true') {
          markUserSelected();
          return;
        }
      }
    });
    optionsContainer.querySelectorAll('[data-option-value], [role="option"]').forEach((el) => {
      observer.observe(el, { attributes: true, attributeFilter: ['aria-selected'] });
    });

    form.addEventListener('variant:change', function () {
      requestAnimationFrame(() => {
        syncAllButtons();
        syncStickyBarSize();
      });
    });

    form.addEventListener('submit', function (e) {
      const buttons = getAllSubmitButtons();
      const anyBlocked = buttons.some((b) => b.dataset.sizeGateBlocked === 'true');
      if (anyBlocked) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        highlightSizeSelector();
      }
    }, true);

    function highlightSizeSelector() {
      sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sizeFieldset.classList.remove('size-gate-highlight');
      void sizeFieldset.offsetWidth;
      sizeFieldset.classList.add('size-gate-highlight');

      const firstOption = sizeFieldset.querySelector('input[type="radio"]:not(:disabled), [role="option"]:not([aria-disabled="true"]), button:not(:disabled)');
      if (firstOption && typeof firstOption.focus === 'function') {
        try { firstOption.focus({ preventScroll: true }); } catch (_) {}
      }
      setTimeout(() => sizeFieldset.classList.remove('size-gate-highlight'), 1600);
    }

    let rebindCount = 0;
    const rebindInterval = setInterval(() => {
      attachClickGuards();
      syncAllButtons();
      syncStickyBarSize();
      rebindCount++;
      if (rebindCount > 20) clearInterval(rebindInterval);
    }, 500);
  }

  function init() {
    document.querySelectorAll('variant-picker[requires-user-selection]').forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
